// Shared verbatim with the public/admin form plugins (customPriceRules.js).
// Currency amounts are entered in units; quotes and snapshots use integer cents.
const MODULE_TYPE = "CUSTOM-PRICE"
const PLUGIN_ID = "form-custom-price/"

function parse(value, fallback) {
    if (typeof value !== "string") return value || fallback
    try { return JSON.parse(value) || fallback } catch (_) { return fallback }
}

function customPriceModules(form) {
    if (form?.simpleEditor === true) {
        const modules = parse(form.modulesContent, [])
        return Array.isArray(modules) ? modules.filter(m => m?.type === MODULE_TYPE).map(m => ({data:m.data || {}, nested:false})) : []
    }
    const modules = []
    const walk = (rows, nested = false) => {
        for (const row of rows || []) for (const cell of row?.cells || []) {
            const id = cell?.plugin?.id || ""
            if (id.startsWith("form-custom-price")) modules.push({data:cell.dataI18n?.default || {}, nested:nested || id !== PLUGIN_ID})
            // Structural rows/columns are allowed, repeating/conditional fields aren't.
            walk(cell.rows, nested || id.startsWith("form-"))
        }
    }
    walk(parse(form?.content, {}).rows)
    return modules
}

function configurationError(form) {
    const modules = customPriceModules(form)
    if (!modules.length) return null
    if (modules.length !== 1) return "CUSTOM_PRICE_DUPLICATE"
    if (modules[0].nested || form?.type !== "COMPLEX") return "CUSTOM_PRICE_PLACEMENT"
    if (["MONTHLY", "ABONNEMENT"].includes(form?.paiementType)) return "CUSTOM_PRICE_MONTHLY"
    const amount = modules[0].data.minimumAmount ?? 1
    if (amountCents(amount) === null) return "CUSTOM_PRICE_MINIMUM"
    return null
}

function customPriceConfig(form) {
    if (!form?.withPaiement || configurationError(form)) return null
    const module = customPriceModules(form)[0]
    if (!module) return null
    return {
        ...module.data,
        minimumCents:Math.round((module.data.minimumAmount ?? 1) * 100),
        allowHigher:module.data.allowHigher === true,
        allowZero:module.data.allowZero === true,
    }
}

function amountCents(value) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null
    const cents = Math.round(value * 100)
    if (!Number.isSafeInteger(cents) || Math.abs(value * 100 - cents) > 0.000001) return null
    return cents
}

function quoteCustomPrice({form, values = {}, devise, baseDevise, productPrice}) {
    const config = customPriceConfig(form)
    if (!config) return null
    let products = 0, donations = 0, eligibleProducts = 0, reduced = 0
    const lines = []
    for (const p of form.products || []) {
        const rawQuantity = values[p.id + "_quantity"]
        if (rawQuantity != null && !["number", "string"].includes(typeof rawQuantity)) return {error:"CUSTOM_PRICE_QUANTITY"}
        if (typeof rawQuantity === "string" && !/^\d+$/.test(rawQuantity)) return {error:"CUSTOM_PRICE_QUANTITY"}
        const quantity = Number(rawQuantity || 0)
        if (!Number.isSafeInteger(quantity) || quantity < 0) return {error:"CUSTOM_PRICE_QUANTITY"}
        if (!quantity) continue
        const allowed = !Array.isArray(p.allowedDevises) || !p.allowedDevises.length || p.allowedDevises.some(d => typeof d === "string" && d.toLowerCase() === String(devise || baseDevise).toLowerCase())
        if (p.active === false || !allowed) return {error:"CUSTOM_PRICE_QUANTITY"}
        const price = productPrice(p, {devise, baseDevise, withPaiement:true})
        const line = price.effectivePriceCents * quantity
        if (!Number.isSafeInteger(line) || line < 0) return {error:"CUSTOM_PRICE_AMOUNT"}
        products += line
        reduced += price.discountCents * quantity
        if (p.cerfa || form.cerfa) eligibleProducts += line
        lines.push([p.id, quantity, price.effectivePriceCents])
    }
    for (const [key, value] of Object.entries(values)) {
        if (!key.startsWith("[FREE_AMOUNT]_")) continue
        if (value == null || value === "") continue
        const cents = amountCents(value)
        if (cents === null) return {error:"CUSTOM_PRICE_AMOUNT"}
        donations += cents
    }
    const promo = (form.promotionalCodes || []).find(c => c?.promotionalCode === values.__promo)
    const percent = Math.min(100, Math.max(0, Number(promo?.reducedPercent) || 0))
    const beforePromo = products + donations
    const promoReduction = Math.round(beforePromo * percent / 100)
    const referenceProducts = products - Math.round(products * percent / 100)
    const referenceTotal = beforePromo - promoReduction
    const donationTotal = referenceTotal - referenceProducts
    const eligibleAfterPromo = eligibleProducts - Math.round(eligibleProducts * percent / 100)
    const referenceKey = JSON.stringify([String(devise || baseDevise).toLowerCase(), lines.sort((a,b) => String(a[0]).localeCompare(String(b[0]))), values.__promo || null, percent, config.minimumCents, config.allowZero, config.allowHigher])
    const input = values.__customPrice
    const enabled = input?.enabled === true
    let error = null
    let chosenProducts = referenceProducts
    if (enabled) {
        const chosen = amountCents(input.amount)
        if (referenceProducts <= 0) error = "CUSTOM_PRICE_NO_PRODUCTS"
        else if (input.referenceKey !== referenceKey) error = "CUSTOM_PRICE_RECONFIRM"
        else if (chosen === null) error = "CUSTOM_PRICE_AMOUNT"
        else if (chosen === 0 && !config.allowZero) error = "CUSTOM_PRICE_ZERO"
        else if (chosen !== 0 && chosen < Math.min(referenceProducts, Math.max(1, config.minimumCents))) error = "CUSTOM_PRICE_TOO_LOW"
        else if (!config.allowHigher && chosen > referenceProducts) error = "CUSTOM_PRICE_TOO_HIGH"
        if (!error) chosenProducts = chosen
    }
    const adjustment = chosenProducts - referenceProducts
    const total = chosenProducts + donationTotal
    if (![total, referenceTotal, reduced].every(Number.isSafeInteger)) error = "CUSTOM_PRICE_AMOUNT"
    return {
        config, error, enabled, referenceKey, referenceProducts, referenceTotal,
        donationTotal, chosenProducts, adjustment, total,
        minimumCents:Math.min(referenceProducts, Math.max(config.allowZero ? 0 : 1, config.minimumCents)),
        totalReduced:reduced + promoReduction + Math.max(0, -adjustment),
        // Increasing a ticket price doesn't automatically turn the extra into a donation.
        totalCerfa:donationTotal + (referenceProducts > 0 ? Math.round(eligibleAfterPromo * Math.min(1, chosenProducts / referenceProducts)) : 0),
    }
}

function snapshotCustomPrice(quote) {
    const {referenceProducts, referenceTotal, chosenProducts, donationTotal, adjustment} = quote
    return {referenceProducts, referenceTotal, chosenProducts, donationTotal, adjustment, customized:quote.enabled}
}

export {MODULE_TYPE, PLUGIN_ID, customPriceModules, configurationError, customPriceConfig, amountCents, quoteCustomPrice, snapshotCustomPrice}
